import React, { useRef } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { aselSelector, closeView, viewPositionSelector } from "~redux/slices/edstSlice";
import { flightplanSelector } from "~redux/slices/aircraftSlice";
import { useDragging } from "hooks/useDragging";
import {
  baseBorderColor,
  colorNameMap,
  edstFontDim,
  edstFontName,
  getBitmapTextStyles,
  processEramMessage,
  useCenterCursor,
  useFocused,
  useInputProps,
  usePixiOnClickOutside,
} from "@poscon/shared-frontend";
import { useZIndex } from "hooks/useZIndex";
import type { Container as PixiContainer } from "pixi.js";
import { MenuTitleBar } from "components/utils/MenuTitleBar";
import type { EramFlightplan } from "@poscon/shared-types/eram";
import { formatCoordinationTime, formatSpeed, stringToParsedTokenArray } from "@poscon/shared-types/eram";
import { cyan, defaultTint } from "~/utils/constants";
import { EdstButton } from "components/utils/EdstButton";
import { EdstInput } from "components/utils/EdstInput";
import { openViewThunk } from "~redux/thunks/openViewThunk";
import { formatBeaconCode, formatRoute } from "@poscon/shared-types";
import { InteractiveContainer } from "../utils/InteractiveContainer";

const view = "TEMPLATE_MENU";

const fontName = edstFontName;
const tint = defaultTint;
const { width: fontWidth, height: fontHeight } = edstFontDim;

const width = 88 * fontWidth;
const height = 18 * fontHeight;

const aidX = fontWidth * 2;
const aidWidth = fontWidth * 8;
const numX = aidX + aidWidth + 8 * 2;
const numWidth = fontWidth * 3;
const saiX = numX + numWidth + 8 * 2;
const saiWidth = fontWidth * 3;
const typeX = saiX + saiWidth + 8 * 2;
const typeWidth = fontWidth * 5;
const equipX = typeX + typeWidth + 8 * 2;
const equipWidth = fontWidth * 6 + 2;
const bcnX = equipX + equipWidth + 8 * 2;
const bcnWidth = fontWidth * 5;
const spdX = bcnX + bcnWidth + 8 * 2;
const spdWidth = fontWidth * 5;
const fixX = spdX + spdWidth + 8 * 2;
const fixWidth = fontWidth * 14;
const timX = fixX + fixWidth + 8 * 2;
const timWidth = fontWidth * 8;
const altX = timX + timWidth + 8 * 2;
const altWidth = width - altX - 20;

const inputWidth = width - aidX - 20;

export const TemplateMenu = () => {
  const asel = useRootSelector(aselSelector);
  const fp = useRootSelector((state) => (asel ? flightplanSelector(state, asel.fpId) : null));
  return <TemplateMenuActual fp={fp} key={fp?.id} />;
};

type TemplateMenuProps = {
  fp: EramFlightplan | null;
};
const TemplateMenuActual = ({ fp }: TemplateMenuProps) => {
  const dispatch = useRootDispatch();
  const asel = useRootSelector(aselSelector);
  const pos = useRootSelector((state) => viewPositionSelector(state, view));
  const ref = useRef<PixiContainer>(null);
  const zIndex = useZIndex(view, ref);
  const focused = useFocused(ref);
  useCenterCursor(ref);
  const { startDrag } = useDragging(ref, view, "mouseup");
  const formattedRoute = fp ? formatRoute(fp.route, fp.departure, fp.destination, fp.routeIsTruncated) : "";

  usePixiOnClickOutside(ref, () => dispatch(closeView("TEMPLATE_MENU")));

  const aidInputProps = useInputProps(`${view}/aid`, fp?.callsign ?? "", undefined, {
    maxLength: 7,
    focusOnMount: false,
  });
  const numInputProps = useInputProps(
    `${view}/numOfAircraft`,
    fp?.numOfAircraft.toString() ?? "",
    undefined,
    {
      maxLength: 2,
      focusOnMount: false,
    },
  );
  const saiInputProps = useInputProps(`${view}/sai`, "", undefined, {
    maxLength: 2,
    focusOnMount: false,
  });
  const typeInputProps = useInputProps(`${view}/aircraftType`, fp?.aircraftType ?? "", undefined, {
    maxLength: 4,
    focusOnMount: false,
  });
  const equipInputProps = useInputProps(`${view}/equipment`, fp?.equipmentQualifier ?? "", undefined, {
    maxLength: 1,
    focusOnMount: false,
  });
  const beaconInputProps = useInputProps(
    `${view}/beacon`,
    fp?.squawk ? formatBeaconCode(fp.squawk) : "",
    undefined,
    {
      maxLength: 4,
      focusOnMount: false,
    },
  );
  const spdInputProps = useInputProps(
    `${view}/speed`,
    fp?.actualSpeed ? formatSpeed(fp.actualSpeed) : "",
    undefined,
    {
      maxLength: 4,
      focusOnMount: false,
    },
  );
  const fixInputProps = useInputProps(`${view}/fix`, fp?.coordinationFix?.fix ?? "", undefined, {
    maxLength: 11,
    focusOnMount: false,
  });
  const timInputProps = useInputProps(
    `${view}/time`,
    fp?.coordinationFix ? formatCoordinationTime(fp.coordinationFix) : "",
    undefined,
    { maxLength: 5, focusOnMount: false },
  );
  const altInputProps = useInputProps(
    `${view}/alt`,
    fp?.assignedAltitude?.simple?.toString() ?? "",
    undefined,
    {
      maxLength: 7,
      focusOnMount: false,
    },
  );
  // const route = formattedRoute?.concat(fp?.destination ? appendDownArrowToString(fp.destination) : "");

  const inputChunkSize = Math.floor((inputWidth - 4) / fontWidth);
  const routeInputProps = useInputProps(`${view}/route`, formattedRoute ?? "", undefined, {
    focusOnMount: false,
  });
  const rmkInputProps = useInputProps(`${view}/remarks`, "", undefined, {
    focusOnMount: false,
  });

  const send = () => {
    if (fp) {
      const msg = `AM ${fp?.cid ?? fp?.callsign}`;
      // TODO: add updated fields to msg
      dispatch(processEramMessage(stringToParsedTokenArray(msg), "EDST", true));
    }
  };

  return (
    <InteractiveContainer ref={ref} x={pos.x} y={pos.y} zIndex={zIndex} sortableChildren>
      <MenuTitleBar
        title={`${asel ? "Amendment" : "Flight Plan"} Template`}
        width={width}
        fill={focused ? cyan : colorNameMap.mediumGrey}
        withX={false}
        onTitleMouseDown={startDrag}
        view="TEMPLATE_MENU"
      />
      <container y={fontHeight + 2}>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: baseBorderColor });
          }}
        />
        <container y={12}>
          <bitmapText text="AID" x={aidX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <bitmapText text="NUM" x={numX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <bitmapText text="SAI" x={saiX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <bitmapText text="TYPE" x={typeX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <EdstButton
            text="Eqp..."
            x={equipX}
            y={-3}
            onmousedown={() => dispatch(openViewThunk("EQUIPMENT_TEMPLATE_MENU", ref.current))}
          />
          <bitmapText text="BCN" x={bcnX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <bitmapText text="SPD" x={spdX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <bitmapText text="FIX" x={fixX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <bitmapText text="TIM" x={timX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <bitmapText text="ALT" x={altX + 2} style={{ ...getBitmapTextStyles(fontName), fill: tint }} />
          <EdstButton text="More..." x={width - fontWidth * 8 - 10} y={-3} />
        </container>
        <container y={20 + fontHeight + 2}>
          <EdstInput x={aidX} width={aidWidth} {...aidInputProps} />
          <EdstInput x={numX} width={numWidth} {...numInputProps} />
          <EdstInput x={saiX} width={saiWidth} {...saiInputProps} />
          <EdstInput x={typeX} width={typeWidth} {...typeInputProps} />
          <EdstInput x={equipX} width={equipWidth} {...equipInputProps} />
          <EdstInput x={bcnX} width={bcnWidth} {...beaconInputProps} />
          <EdstInput x={spdX} width={spdWidth} {...spdInputProps} />
          <EdstInput x={fixX} width={fixWidth} {...fixInputProps} />
          <EdstInput x={timX} width={timWidth} {...timInputProps} />
          <EdstInput x={altX} width={altWidth} {...altInputProps} />
        </container>
        <bitmapText
          text="RTE"
          x={aidX + 2}
          y={20 + fontHeight * 3}
          style={{ ...getBitmapTextStyles(fontName), fill: tint }}
        />
        <EdstInput
          x={aidX}
          y={28 + fontHeight * 4}
          width={inputWidth}
          height={fontHeight * 4}
          chunkSize={inputChunkSize}
          {...routeInputProps}
        />
        <bitmapText
          text="RMK"
          x={aidX + 2}
          y={44 + fontHeight * 8}
          style={{ ...getBitmapTextStyles(fontName), fill: tint }}
        />
        <EdstInput
          x={aidX}
          y={52 + fontHeight * 9}
          width={inputWidth}
          height={fontHeight * 3}
          chunkSize={inputChunkSize}
          {...rmkInputProps}
        />

        <EdstButton text="Send" onmousedown={send} x={aidX} y={height - fontHeight - 13} />
        <EdstButton
          text="Cancel"
          onmousedown={() => {
            dispatch(closeView(view));
          }}
          x={width - fontWidth * 7 - 10}
          y={height - fontHeight - 13}
        />
      </container>
    </InteractiveContainer>
  );
};
